import { useEffect, useRef, useState } from "react";
import { MessageSquare, Pencil, Rocket, Zap, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

const HowItWorks = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: MessageSquare,
      title: "Tell Me Your Vision",
      description: "Share your ideas, goals, and inspiration.",
      number: "01",
    },
    {
      icon: Pencil,
      title: "Design & Build",
      description: "I craft your custom site with progress updates.",
      number: "02",
    },
    {
      icon: Rocket,
      title: "Launch",
      description: "Your site is optimized, tested, and live.",
      number: "03",
    },
  ];

  return (
    <section id="services" ref={sectionRef} className={`${isMobile ? "py-10" : "py-16"} relative overflow-hidden`}>
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-card via-background to-background" />

      <div className={`container mx-auto ${isMobile ? "px-3" : "px-6"} relative`}>
        {/* Header */}
        <div className={`text-center ${isMobile ? "mb-4" : "mb-6"}`}>
          <p
            className={`text-primary font-medium tracking-widest uppercase ${isMobile ? "mb-2 text-xs" : "mb-4"} transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Simple Process
          </p>
          <h2
            className={`${isMobile ? "text-xl" : "text-4xl md:text-6xl lg:text-7xl"} font-black ${
              isVisible ? "animate-cinematic-fade-up" : "opacity-0"
            }`}
            style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
          >
            How I Build Your {isMobile && <br />}<span className="text-gradient-static">Custom Website</span>
          </h2>
        </div>

        {/* Fast Turnaround Badge */}
        <div
          className={`flex justify-center ${isMobile ? "mb-6" : "mb-10"} transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className={`inline-flex items-center ${isMobile ? "gap-2 px-3 py-2" : "gap-3 px-6 py-3"} bg-primary/10 border border-primary/30 rounded-full`}>
            <Zap size={isMobile ? 14 : 20} className="text-primary" />
            <span className={`${isMobile ? "text-xs" : "text-lg"} font-semibold text-primary`}>
              Extremely Fast Turnaround
            </span>
          </div>
        </div>

        {/* Steps */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 ${isMobile ? "gap-3" : "gap-4 sm:gap-6 lg:gap-8"}`}>
          {steps.map((step, index) => (
            <div
              key={step.title}
              className={`group relative ${isMobile ? "p-4" : "p-6 sm:p-8"} border border-border bg-card/50 backdrop-blur-sm rounded-lg transition-all duration-300 ease-out hover:border-primary/50 hover:bg-card hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_hsl(var(--primary)/0.3)] ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
              }`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              {/* Number */}
              <span className={`absolute ${isMobile ? "top-3 right-3 text-3xl" : "top-4 right-4 sm:top-8 sm:right-8 text-4xl sm:text-6xl"} font-black text-muted/30 transition-colors duration-300 group-hover:text-primary/20`}>
                {step.number}
              </span>

              {/* Step indicator line - horizontal on desktop */}
              {index < steps.length - 1 && (
                <div className="hidden sm:block absolute top-1/2 -right-3 lg:-right-4 w-6 lg:w-8 h-0.5 bg-border z-10" />
              )}

              {/* Icon */}
              <div className={`${isMobile ? "w-10 h-10 mb-3" : "w-12 h-12 sm:w-16 sm:h-16 mb-4 sm:mb-6"} rounded-full bg-primary/10 flex items-center justify-center transition-all duration-300 group-hover:bg-primary group-hover:scale-110`}>
                <step.icon
                  size={isMobile ? 18 : 24}
                  className="text-primary transition-colors duration-300 group-hover:text-primary-foreground sm:[&]:w-7 sm:[&]:h-7"
                />
              </div>

              {/* Content */}
              <h3 className={`${isMobile ? "text-sm" : "text-lg sm:text-xl"} font-bold ${isMobile ? "mb-1" : "mb-2 sm:mb-3"}`}>{step.title}</h3>
              <p className={`${isMobile ? "text-xs" : "text-sm sm:text-base"} text-muted-foreground leading-relaxed`}>
                {step.description}
              </p>

              {/* Hover Line */}
              <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-primary to-accent transition-all duration-500 group-hover:w-full rounded-b-lg" />
            </div>
          ))}
        </div>

        {/* Read More Link */}
        <div
          className={`text-center ${isMobile ? "mt-6" : "mt-10"} transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "600ms" }}
        >
          <Link
            to="/how-it-works-web-design-process"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline transition-all duration-300 group"
          >
            <span className={isMobile ? "text-sm" : "text-base"}>Read More About My Process</span>
            <ArrowRight size={isMobile ? 16 : 18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;